import {
  generateUniqueId,
  getLocalStorageMemoizedValue,
  urlJoin,
} from '@digital-spiders/misc-utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PageDocument } from '../../types/types';
import { getIsIOS, useCloser } from '../../utils/projectUtils';
import { getPageDocumentUrl } from '../../utils/sanity';
import { withDataLayer } from '../../utils/utils';

const MIN_CALENDAR_HEIGHT = 380;
const MAX_CALENDAR_HEIGHT = 760;

declare global {
  interface Window {
    hbspt: any;
    hsEmbedCode: any;
  }
}

export interface HubSpotCalendarProps {
  pageToGoAfterSubmit?: PageDocument;
  calendarId: string;
  className?: string;
  onLoad?: () => void;
}

function HubSpotCalendar(props: HubSpotCalendarProps): React.ReactElement {
  const { calendarId, className, onLoad, pageToGoAfterSubmit } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const hubspotScheduleEventTriggered = useRef(false);
  const [finalCalendarId, setFinalCalendarId] = useState<string>('');
  const [iOSCalendarHeight, setIOSCalendarHeight] = useState<number | null>(null);

  const closer = useCloser();

  useEffect(() => {
    const updateCalendarHeight = () => {
      if (getIsIOS()) {
        const height = Math.max(
          Math.min(Math.floor(window.innerHeight - 150), MAX_CALENDAR_HEIGHT),
          MIN_CALENDAR_HEIGHT,
        );
        setIOSCalendarHeight(height);
      }
    };

    updateCalendarHeight();
  }, []);

  // Get calendar parameter from URL
  const calendarFromUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('calendar');
    }
    return null;
  }, []);

  // Determine final calendar ID and mark as ready
  useEffect(() => {
    // If we have URL param, use it immediately (highest priority)
    if (calendarFromUrl) {
      setFinalCalendarId(calendarFromUrl);
      return;
    }

    // If closer is loaded and has hubspotCalendarId, use it
    if (closer && closer.hubspotCalendarId) {
      setFinalCalendarId(closer.hubspotCalendarId);
      return;
    }

    // If closer loading is complete (not null/undefined) but no hubspotCalendarId, use default
    if (closer !== undefined) {
      setFinalCalendarId(calendarId);
      return;
    }

    // If no URL param and closer is still loading (undefined), wait
  }, [closer, calendarFromUrl, calendarId]);

  const hubSpotCalendarEmbedUrl = finalCalendarId
    ? `https://meetings.hubspot.com/${finalCalendarId}?embed=true`
    : undefined;

  useEffect(() => {
    if (!finalCalendarId) return;

    // Load HubSpot meetings embed script (always fresh)
    const loadHubSpotScript = () => {
      return new Promise<void>(resolve => {
        const existingScript = document.querySelector(
          'script[src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"]',
        );

        if (existingScript) {
          existingScript.remove();
        }
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => resolve(); // Still resolve to prevent hanging
        document.head.appendChild(script);
      });
    };

    // Initialize HubSpot calendar
    const initializeCalendar = async () => {
      await loadHubSpotScript();

      // Set the data-src attribute for HubSpot to pick up
      if (containerRef.current && hubSpotCalendarEmbedUrl) {
        containerRef.current.setAttribute('data-src', hubSpotCalendarEmbedUrl);

        if (onLoad) {
          onLoad();
        }
      }
    };

    // Add message listener for HubSpot booking completion
    const handleHubSpotMessage = (event: MessageEvent) => {
      // HubSpot meetings embed sends messages from meetings.hubspot.com domain
      if (event.origin.includes('meetings.hubspot.com') && event.data) {
        const eventData = event.data;

        // Check for booking completion - HubSpot sends meetingBookSucceeded: true
        if (eventData.meetingBookSucceeded === true && !hubspotScheduleEventTriggered.current) {
          withDataLayer(dataLayer => {
            dataLayer.push({
              event: 'hubspot-calendar-schedule',
              calendar: finalCalendarId,
              uniqueId: getLocalStorageMemoizedValue(
                'randomly_generated_unique_id',
                generateUniqueId,
              ),
            });
          });
          hubspotScheduleEventTriggered.current = true;

          if (pageToGoAfterSubmit) {
            window.location.href = urlJoin(getPageDocumentUrl(pageToGoAfterSubmit));
          }
        } else if (eventData.scrollParent && getIsIOS()) {
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;
            const offsetPosition = elementTop - 80;

            // Smooth scroll the calendar with some offset from top
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            });
          }
        }
      }
    };

    window.addEventListener('message', handleHubSpotMessage, false);

    initializeCalendar();

    return () => {
      // Cleanup
      window.removeEventListener('message', handleHubSpotMessage, false);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      // Remove the script on cleanup
      const existingScript = document.querySelector(
        'script[src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"]',
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [finalCalendarId, pageToGoAfterSubmit?.slug.current]);

  return (
    <div
      ref={containerRef}
      className={`meetings-iframe-container ${className || ''}`}
      data-src={hubSpotCalendarEmbedUrl}
      style={
        iOSCalendarHeight !== null
          ? { height: `${iOSCalendarHeight}px`, overflow: 'auto' }
          : undefined
      }
    ></div>
  );
}

export default HubSpotCalendar;
